import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { apiService } from '../services/api';
import { HorsePropertiesRead } from '../types/api';

interface HorseTraitsProps {
  horseId: string;
  inCollection: boolean;
  isEditing?: boolean;
  initialProperties?: HorsePropertiesRead | null;
}

export const HorseTraits: React.FC<HorseTraitsProps> = ({ horseId, inCollection, isEditing = false, initialProperties }) => {
  const [traits, setTraits] = useState<string[]>(() => {
    if (initialProperties?.content) {
      return initialProperties.content
        .split('\n')
        .map(line => {
          const match = line.match(/^(good|bad|neutral)\|(.*)$/i);
          return match ? match[2].trim() : line.trim();
        })
        .filter(line => line.length > 0)
        .slice(0, 20);
    }
    return [];
  });
  const [properties, setProperties] = useState<HorsePropertiesRead | null>(initialProperties ?? null);
  const [loading, setLoading] = useState(initialProperties === undefined);
  const [saving, setSaving] = useState(false);
  const [newTraitText, setNewTraitText] = useState('');
  const [isAddingTrait, setIsAddingTrait] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadProperties = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getHorseProperties(horseId);
      
      if (data) {
        setProperties(data);
        const parsedTraits = parseTraitsFromProperties(data.content);
        setTraits(parsedTraits);
      } else {
        setProperties(null);
        setTraits([]);
      }
    } catch (error) {
      console.warn('Failed to load properties for traits:', error);
      setProperties(null);
      setTraits([]);
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  const skipInitialLoad = useRef(initialProperties !== undefined);
  useEffect(() => {
    if (skipInitialLoad.current) {
      skipInitialLoad.current = false;
      return;
    }
    if (apiService.isAuthenticated()) {
      loadProperties();
    } else {
      setTraits([]);
    }
  }, [loadProperties]);

  // Parse traits from content - simple text per line
  const parseTraitsFromProperties = (content: string): string[] => {
    if (!content) return [];

    return content
      .split('\n')
      .map((line) => {
        // Handle legacy format with sentiment prefix
        const match = line.match(/^(good|bad|neutral)\|(.*)$/i);
        if (match) {
          return match[2].trim();
        }
        return line.trim();
      })
      .filter((line) => line.length > 0)
      .slice(0, 20);
  };

  useEffect(() => {
    if (isAddingTrait && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAddingTrait]);

  if (!apiService.isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-wrap gap-[3px]">
        <div className="px-[10px] py-[3px] bg-gray-100 rounded-[13px] text-sm text-gray-500">
          Loading traits...
        </div>
      </div>
    );
  }

  const saveTraits = async (newTraits: string[]) => {
    const validTraits = newTraits
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    setSaving(true);
    try {
      if (validTraits.length === 0) {
        if (properties) {
          await apiService.deleteHorseProperties(horseId).catch(() => undefined);
        }
        setProperties(null);
        setTraits([]);
      } else {
        const content = validTraits.join('\n');

        if (properties) {
          const updated = await apiService.updateHorseProperties(horseId, {
            title: properties.title || 'Horse Traits',
            content,
          });
          setProperties(updated);
        } else {
          const created = await apiService.createHorseProperties(horseId, {
            title: 'Horse Traits',
            content,
          });
          setProperties(created);
        }
        setTraits(validTraits);
      }
    } catch (err) {
      console.error('Failed to save traits:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTrait = async () => {
    const trimmed = newTraitText.trim();
    if (!trimmed) {
      setIsAddingTrait(false);
      setNewTraitText('');
      return;
    }
    
    const newTraits = [...traits, trimmed];
    await saveTraits(newTraits);
    setNewTraitText('');
    setIsAddingTrait(false);
  };

  const handleRemoveTrait = async (index: number) => {
    if (saving) return;
    const newTraits = traits.filter((_, i) => i !== index);
    await saveTraits(newTraits);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTrait();
    } else if (e.key === 'Escape') {
      setIsAddingTrait(false);
      setNewTraitText('');
    }
  };

  // No traits - show + button in edit mode only
  if (traits.length === 0) {
    if (isEditing) {
      return (
        <div className="flex items-start gap-2">
          <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Traits:</span>
          <div className="flex flex-wrap gap-[3px] items-center flex-1 min-w-0">
            {isAddingTrait ? (
              <input
                ref={inputRef}
                type="text"
                value={newTraitText}
                onChange={(e) => setNewTraitText(e.target.value)}
                onBlur={handleAddTrait}
                onKeyDown={handleKeyDown}
                className="bg-[#ebf6e4] h-[32px] px-[10px] py-[6px] rounded-[5px] text-sm text-[#233454] border-none outline-none min-w-[120px]"
                placeholder="Type trait..."
                maxLength={50}
                disabled={saving}
              />
            ) : (
              <button
                className="bg-[#f2fff3] h-[32px] w-[32px] rounded-[5px] text-sm text-[#233454] hover:bg-[#e5f5e7] transition-colors inline-flex items-center justify-center"
                onClick={() => setIsAddingTrait(true)}
                disabled={saving}
              >
                +
              </button>
            )}
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="flex items-start gap-2">
      <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Traits:</span>
      <div className="flex flex-wrap gap-[3px] items-center flex-1 min-w-0">
        {traits.map((trait, index) => (
            <div
              key={index}
              className="bg-[#f2fff3] h-[32px] px-[10px] rounded-[5px] text-sm text-[#233454] inline-flex items-center gap-1"
          >
            <span>{trait}</span>
            {isEditing && (
              <button
                type="button"
                onClick={() => handleRemoveTrait(index)}
                disabled={saving}
                className="text-gray-400 hover:text-red-600 focus:outline-none disabled:opacity-50 ml-1"
                aria-label="Remove trait"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ))}
        {isEditing && (
          isAddingTrait ? (
            <input
              ref={inputRef}
              type="text"
              value={newTraitText}
              onChange={(e) => setNewTraitText(e.target.value)}
              onBlur={handleAddTrait}
              onKeyDown={handleKeyDown}
              className="bg-[#ebf6e4] h-[32px] px-[10px] py-[6px] rounded-[5px] text-sm text-[#233454] border-none outline-none min-w-[120px]"
              placeholder="Type trait..."
              maxLength={50}
              disabled={saving}
            />
          ) : (
            <button
              className="bg-[#f2fff3] h-[32px] w-[32px] rounded-[5px] text-sm text-[#233454] hover:bg-[#e5f5e7] transition-colors inline-flex items-center justify-center"
              onClick={() => setIsAddingTrait(true)}
              disabled={saving}
            >
              +
            </button>
          )
        )}
      </div>
    </div>
  );
};
